import { t } from 'i18next'
import * as React from 'react'
import { RouteComponentProps, withRouter } from 'react-router'

type SearchBarProps = {
  show: boolean
  hide?: () => void
  searchValue: string
  updateSearchValue: (newValue: string) => void
}

type SearchFormProps = RouteComponentProps &
  Pick<SearchBarProps, 'hide' | 'searchValue' | 'updateSearchValue'> & { label?: string }

export const SearchForm = withRouter((props: SearchFormProps) => (
  <form
    className="search-form"
    onSubmit={(e) => {
      e.preventDefault()
      props.history.push(`/search/${props.searchValue}`)
    }}
  >
    <label className="search-form__label ff--primary">
      {props.label}
      <input
        type="search"
        inputMode="search"
        className="search-form__input"
        placeholder={t('searchbar.placeholder')}
        value={props.searchValue}
        onChange={(e) => props.updateSearchValue(e.target.value)}
      />
    </label>
    <button className="search-form__icon" type="submit">
      <svg className="icon icon--arrow-right" width="14px" height="12px">
        <use xlinkHref="#arrow" />
      </svg>
    </button>
  </form>
))

export const SearchBar = (props: SearchBarProps) => {
  const { show } = props
  const searchbarRef = React.useRef<HTMLElement>(null)

  React.useEffect(() => {
    const lastFocusedElement = document.activeElement
    const escapeHandler = (e: KeyboardEvent) => show && e.key === 'Escape' && props.hide()

    // Set 'inert' on the searchbar when it is hidden to prevent tab navigation
    if (searchbarRef.current) {
      if (show) {
        searchbarRef.current.removeAttribute('inert')
      } else {
        searchbarRef.current.setAttribute('inert', '')
      }
    }
    // Create focus-trap effect by setting inert attribute on .sh__inner, main and footer
    const headerNav = document.querySelector('.sh__inner')
    const main = document.querySelector('#a11y-main')
    const footer = document.querySelector('#a11y-footer')
    if (show) {
      headerNav?.setAttribute('inert', '')
      main?.setAttribute('inert', '')
      footer?.setAttribute('inert', '')
    } else {
      headerNav?.removeAttribute('inert')
      main?.removeAttribute('inert')
      footer?.removeAttribute('inert')
    }

    // Close mobile hamburger menu on Escape
    if (show) {
      window.addEventListener('keydown', escapeHandler, { passive: true })
    }

    return () => {
      if (lastFocusedElement !== null && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus()
      window.removeEventListener('keydown', escapeHandler)
    }
  }, [show])
  return (
    <>
      <section
        ref={searchbarRef}
        id="a11y-searchbar"
        role="dialog"
        aria-modal="true"
        className={`searchbar ${show ? 'searchbar--show' : ''}`}
      >
        <div className="searchbar__inner container container--s gutter--m spacing--m">
          <button className="searchbar__close" onClick={props.hide}>
            <span className="sr-text">{t('general.close')}</span>
            <span className="line line--first" />
            <span className="line line--second" />
          </button>

          <SearchForm
            searchValue={props.searchValue}
            updateSearchValue={props.updateSearchValue}
            label={t('searchbar.label')}
          />
        </div>
      </section>
      <div
        className={`searchbar__overlay ${show ? 'searchbar__overlay--show' : ''}`}
        onClick={props.hide}
        role="presentation"
      />
    </>
  )
}
